import { useCallback, useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import Button from './Button';

const OUTPUT_SIZE = 720;
const MAX_ZOOM = 4;

type Props = {
  src: string;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
  confirming?: boolean;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function coverScale(frame: number, width: number, height: number) {
  if (!width || !height) return 1;
  return Math.max(frame / width, frame / height);
}

function clampOffset(
  offsetX: number,
  offsetY: number,
  scale: number,
  frame: number,
  width: number,
  height: number,
) {
  const maxX = Math.max(0, (width * scale - frame) / 2);
  const maxY = Math.max(0, (height * scale - frame) / 2);
  return {
    x: clamp(offsetX, -maxX, maxX),
    y: clamp(offsetY, -maxY, maxY),
  };
}

export default function AvatarCropper({ src, onCancel, onConfirm, confirming = false }: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const panRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const fittedSrcRef = useRef('');

  const [frame, setFrame] = useState(() =>
    typeof window === 'undefined' ? 320 : Math.min(320, window.innerWidth - 48),
  );
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loadError, setLoadError] = useState('');

  const minScale = coverScale(frame, natural.width, natural.height);
  const maxScale = minScale * MAX_ZOOM;

  const applyScale = useCallback((next: number) => {
    const clamped = clamp(next, minScale, maxScale);
    setScale(clamped);
    setOffset((prev) =>
      clampOffset(prev.x, prev.y, clamped, frame, natural.width, natural.height),
    );
  }, [frame, maxScale, minScale, natural.height, natural.width]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const sync = () => setFrame(el.clientWidth);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      setNatural({ width: image.naturalWidth, height: image.naturalHeight });
      setLoadError('');
    };
    image.onerror = () => setLoadError('Could not read that photo. Try a JPEG or PNG.');
    image.src = src;
  }, [src]);

  useEffect(() => {
    if (!natural.width) return;
    if (fittedSrcRef.current === src) {
      setScale((current) => clamp(current, minScale, maxScale));
      setOffset((prev) =>
        clampOffset(prev.x, prev.y, scaleRef.current, frame, natural.width, natural.height),
      );
      return;
    }
    fittedSrcRef.current = src;
    setScale(coverScale(frame, natural.width, natural.height));
    setOffset({ x: 0, y: 0 });
  }, [frame, maxScale, minScale, natural.height, natural.width, src]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirming) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [confirming, onCancel]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (confirming) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = {
        distance: Math.hypot(a.x - b.x, a.y - b.y),
        scale: scaleRef.current,
      };
      panRef.current = null;
    } else {
      panRef.current = {
        x: event.clientX,
        y: event.clientY,
        ox: offsetRef.current.x,
        oy: offsetRef.current.y,
      };
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size >= 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchRef.current.distance > 0) {
        applyScale(pinchRef.current.scale * (distance / pinchRef.current.distance));
      }
      return;
    }

    if (!panRef.current) return;
    const next = clampOffset(
      panRef.current.ox + (event.clientX - panRef.current.x),
      panRef.current.oy + (event.clientY - panRef.current.y),
      scaleRef.current,
      frame,
      natural.width,
      natural.height,
    );
    setOffset(next);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(event.pointerId);
    pinchRef.current = null;
    if (pointersRef.current.size === 1) {
      const remaining = [...pointersRef.current.values()][0];
      panRef.current = {
        x: remaining.x,
        y: remaining.y,
        ox: offsetRef.current.x,
        oy: offsetRef.current.y,
      };
    } else {
      panRef.current = null;
    }
  };

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (confirming) return;
    event.preventDefault();
    applyScale(scaleRef.current * (event.deltaY < 0 ? 1.08 : 1 / 1.08));
  };

  const handleConfirm = async () => {
    if (!natural.width || confirming) return;
    try {
      const image = new Image();
      image.src = src;
      if (!image.complete) {
        await new Promise<void>((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error('decode'));
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return;
      ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      const sourceWidth = image.naturalWidth || natural.width;
      const sourceHeight = image.naturalHeight || natural.height;
      const imgLeft = frame / 2 + offset.x - (sourceWidth * scale) / 2;
      const imgTop = frame / 2 + offset.y - (sourceHeight * scale) / 2;
      ctx.drawImage(
        image,
        (0 - imgLeft) / scale,
        (0 - imgTop) / scale,
        frame / scale,
        frame / scale,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });
      if (blob) onConfirm(blob);
    } catch {
      setLoadError('Could not crop that photo. Try a JPEG or PNG.');
    }
  };

  const imgLeft = frame / 2 + offset.x - (natural.width * scale) / 2;
  const imgTop = frame / 2 + offset.y - (natural.height * scale) / 2;
  const zoomValue = minScale > 0 ? scale / minScale : 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="avatar-crop-title"
      onClick={confirming ? undefined : onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-5 pb-3">
          <h3 id="avatar-crop-title" className="text-lg font-semibold text-navy">
            Frame your photo
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Drag or pinch to fit your face in the circle.
          </p>
        </div>

        <div className="px-5">
          <div
            ref={frameRef}
            className="relative w-full aspect-square overflow-hidden touch-none select-none bg-transparent"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
          >
            {natural.width > 0 && (
              <img
                src={src}
                alt=""
                draggable={false}
                className="absolute max-w-none pointer-events-none"
                style={{
                  width: natural.width * scale,
                  height: natural.height * scale,
                  left: imgLeft,
                  top: imgTop,
                }}
              />
            )}
            <div
              className="absolute inset-0 pointer-events-none rounded-full border-2 border-navy"
            />
          </div>
          {loadError && <p className="text-xs text-red-500 mt-2">{loadError}</p>}
        </div>

        <div className="flex items-center gap-3 px-5 pt-4">
          <button
            type="button"
            aria-label="Zoom out"
            disabled={confirming || scale <= minScale}
            onClick={() => applyScale(scale / 1.15)}
            className="text-navy disabled:opacity-40"
          >
            <Minus size={18} />
          </button>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoomValue}
            disabled={confirming || !natural.width}
            onChange={(event) => applyScale(minScale * Number(event.target.value))}
            className="flex-1 accent-navy"
            aria-label="Zoom"
          />
          <button
            type="button"
            aria-label="Zoom in"
            disabled={confirming || scale >= maxScale}
            onClick={() => applyScale(scale * 1.15)}
            className="text-navy disabled:opacity-40"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="flex gap-3 p-5">
          <Button variant="ghost" className="flex-1" onClick={onCancel} disabled={confirming}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => { void handleConfirm(); }}
            disabled={!natural.width || !!loadError}
            loading={confirming}
          >
            Use photo
          </Button>
        </div>
      </div>
    </div>
  );
}
